import type { CaptureResult, PostHog, PostHogConfig } from 'posthog-js';
import { consent, type ConsentStore } from '../consent/consent.js';
import { analyticsMayIdentify } from '@krevya/shared';
import {
  analyticsEnvironment,
  isPrivatePath,
  isReplayExcludedPath,
  sanitizeUrlProperties,
  POSTHOG_HOST,
  POSTHOG_KEY,
  type AnalyticsEnvironment,
} from './config.js';

/**
 * Product analytics (PostHog), strictly behind the consent module's answer.
 *
 * Until analytics is allowed, none of PostHog's code is even downloaded: the
 * library is a dynamic import made from inside
 * `consent.whenAllowed('analytics', …)`, so before that there is no script, no
 * cookie, no storage key and no request. Where analytics needs a yes (the
 * strict model) that means after the yes; in the US model it is allowed by
 * default unless the visitor opted out or sends GPC. Either way nothing loads
 * before `/api/geo` has said which model applies. Events the app reports
 * before then are dropped, not queued.
 *
 * Once allowed, the download still waits for the page: it starts when the
 * browser is idle after `load`, never before the first paint. In the US model
 * analytics is allowed the moment the region answers, which is while the
 * landing page is still painting, and the library is the largest thing the
 * page would otherwise fetch then. Events reported while it waits are queued.
 *
 * Analytics and session replay are two decisions. Analytics starts with the
 * recorder disabled unless `consent.replayAllowed()`, which is true only in
 * the strict model after an explicit yes; in the US model the recorder is
 * disabled in the config and never started. Admin screens are never recorded.
 *
 * Withdrawing the answer opts PostHog out, resets it and deletes everything it
 * stored. The consent store then reloads the page, which is the only way to
 * unload code that has already run.
 */

/**
 * The events the app reports, with the properties each may carry. Plain names
 * a person reading a funnel understands; never an email, a name or free text.
 */
export type AnalyticsEvents = {
  signup_completed: Record<string, never>;
  store_connected: { provider: 'shopify' };
  onboarding_step_completed: { step: 'connect' | 'products' | 'brand' | 'create' };
  first_creative_started: Record<string, never>;
  checkout_opened: { plan: string; interval: 'month' | 'year'; currency: string | null };
  plan_activated: Record<string, never>;
};
export type AnalyticsEvent = keyof AnalyticsEvents;

/** The browser leaves right after these, so they are sent at once rather than batched. */
const SENT_AT_ONCE: ReadonlySet<AnalyticsEvent> = new Set(['checkout_opened']);

/** What this module uses of PostHog; narrow, so the tests can stand in for it. */
export type PostHogClient = Pick<
  PostHog,
  | 'init'
  | 'capture'
  | 'identify'
  | 'group'
  | 'register'
  | 'reset'
  | 'opt_out_capturing'
  | 'startSessionRecording'
  | 'stopSessionRecording'
>;

type KeyValue = Pick<Storage, 'key' | 'length' | 'removeItem'>;

export type AnalyticsDeps = {
  consent: Pick<ConsentStore, 'allows' | 'whenAllowed' | 'subscribe' | 'region' | 'replayAllowed'>;
  environment: AnalyticsEnvironment | null;
  /** Fetches the library. Called at most once, and only after consent. */
  load: () => Promise<PostHogClient>;
  /** Runs `work` once the page has loaded and the browser has a moment to spare. */
  whenIdle: (work: () => void) => void;
  pathname: () => string;
  /** Calls back on every in-app navigation; returns an unsubscribe. */
  onNavigate: (listener: () => void) => () => void;
  local: () => KeyValue | null;
  session: () => KeyValue | null;
  cookieNames: () => string[];
  expireCookie: (name: string) => void;
};

/** PostHog's own keys and cookies: `ph_<key>_posthog`, `__ph_opt_in_out_<key>`, and the like. */
const POSTHOG_STORAGE = /^_{0,2}ph_/;

/** How many events may wait for the library while it downloads. */
const PENDING_LIMIT = 50;

export function createAnalytics(deps: AnalyticsDeps) {
  let client: PostHogClient | null = null;
  let loading = false;
  let started = false;
  let identity: { userId: string; workspaceId: string } | null = null;
  /** Work asked for after consent but before the library arrived. */
  let pending: ((ph: PostHogClient) => void)[] = [];
  /** The recorder was stopped by us on an excluded screen and is ours to restart. */
  let pausedForPrivate = false;

  function purgeStorage(): void {
    for (const storage of [deps.local(), deps.session()]) {
      if (!storage) continue;
      const keys: string[] = [];
      try {
        for (let index = 0; index < storage.length; index += 1) {
          const key = storage.key(index);
          if (key && POSTHOG_STORAGE.test(key)) keys.push(key);
        }
        for (const key of keys) storage.removeItem(key);
      } catch {
        // Storage that cannot be read holds nothing of ours either.
      }
    }
    for (const name of deps.cookieNames()) {
      if (POSTHOG_STORAGE.test(name)) deps.expireCookie(name);
    }
  }

  /**
   * Drops anything captured on a staff screen, recordings included, and keeps
   * a staff address out of the next page's "previous page" properties.
   */
  const beforeSend = (event: CaptureResult | null): CaptureResult | null => {
    if (!event) return null;
    if (isPrivatePath(deps.pathname())) return null;
    const properties = event.properties as Record<string, unknown> | undefined;
    const current = properties?.['$pathname'];
    if (typeof current === 'string' && isPrivatePath(current)) return null;
    const previous = properties?.['$prev_pageview_pathname'];
    if (properties && typeof previous === 'string' && isPrivatePath(previous)) {
      for (const key of Object.keys(properties)) {
        if (key.startsWith('$prev_pageview_')) delete properties[key];
      }
    }
    // Tokens, codes, emails and OAuth state never leave in an address.
    if (properties) sanitizeUrlProperties(properties);
    const set = (event as { $set?: unknown }).$set;
    if (set && typeof set === 'object') sanitizeUrlProperties(set as Record<string, unknown>);
    const setOnce = (event as { $set_once?: unknown }).$set_once;
    if (setOnce && typeof setOnce === 'object') sanitizeUrlProperties(setOnce as Record<string, unknown>);
    return event;
  };

  /** Session replay may run in this page at all (never on a default, never in the US model). */
  const replayAllowed = () => deps.consent.replayAllowed();

  /** The recorder's half of the config, decided apart from analytics itself. */
  function replayConfig(): Pick<PostHogConfig, 'session_recording' | 'disable_session_recording'> {
    return {
      session_recording: { maskAllInputs: true },
      // Off unless replay is allowed, and off when arriving straight on a
      // screen that is never recorded (admin, sign-in, billing, settings).
      disable_session_recording: !replayAllowed() || isReplayExcludedPath(deps.pathname()),
    };
  }

  function config(): Partial<PostHogConfig> {
    return {
      api_host: POSTHOG_HOST,
      defaults: '2026-05-30',
      // No person profile for an anonymous visitor; only a signed-in user id
      // (never a name or an email) ever becomes one.
      person_profiles: 'identified_only',
      // Only ever reached once analytics is allowed, so persistent storage is too.
      persistence: 'localStorage+cookie',
      // The router moves with history.pushState, which this follows.
      capture_pageview: 'history_change',
      respect_dnt: true,
      ...replayConfig(),
      before_send: beforeSend,
    };
  }

  /** Pauses the recorder on an excluded screen and resumes it after; never touches it where replay is not allowed. */
  function followRoute(): void {
    if (!client || !replayAllowed()) return;
    const onPrivate = isReplayExcludedPath(deps.pathname());
    if (onPrivate && !pausedForPrivate) {
      pausedForPrivate = true;
      client.stopSessionRecording();
    } else if (!onPrivate && pausedForPrivate) {
      pausedForPrivate = false;
      client.startSessionRecording();
    }
  }

  function applyIdentity(ph: PostHogClient): void {
    if (!identity) return;
    // Anonymous where the policy says so (Türkiye, or a country not known):
    // no user or workspace id is sent at all.
    if (!analyticsMayIdentify(deps.consent.region()?.country)) return;
    // The id alone: no email or name ever reaches PostHog.
    ph.identify(identity.userId);
    ph.group('workspace', identity.workspaceId);
  }

  /** Allowed: the download waits for an idle moment after `load`, and events queue meanwhile. */
  function schedule(): void {
    if (loading || client) return;
    loading = true;
    deps.whenIdle(() => void boot());
  }

  async function boot(): Promise<void> {
    // Withdrawn while waiting for the idle moment: nothing is fetched at all.
    if (!deps.consent.allows('analytics')) {
      loading = false;
      pending = [];
      return;
    }
    loading = true;
    let ph: PostHogClient;
    try {
      ph = await deps.load();
    } catch {
      // A blocked or failed download leaves the app exactly as it was.
      loading = false;
      pending = [];
      return;
    }
    loading = false;
    // The answer may have changed while the library downloaded.
    if (!deps.consent.allows('analytics')) {
      pending = [];
      purgeStorage();
      return;
    }
    ph.init(POSTHOG_KEY, config());
    if (deps.environment) ph.register({ environment: deps.environment });
    client = ph;
    pausedForPrivate = isReplayExcludedPath(deps.pathname());
    applyIdentity(ph);
    const queued = pending;
    pending = [];
    for (const run of queued) run(ph);
  }

  function shutdown(): void {
    const ph = client;
    client = null;
    pending = [];
    pausedForPrivate = false;
    if (ph) {
      try {
        ph.opt_out_capturing();
        ph.reset();
      } catch {
        // Whatever state it was in, its storage is deleted below.
      }
    }
    // Last, so nothing written by the opt-out or the reset survives it.
    purgeStorage();
  }

  /** Runs `work` against PostHog now, after it has loaded, or never without consent. */
  function withClient(work: (ph: PostHogClient) => void): void {
    if (!deps.environment || !deps.consent.allows('analytics')) return;
    if (client) {
      work(client);
      return;
    }
    if (loading && pending.length < PENDING_LIMIT) pending.push(work);
  }

  return {
    /** Arms the gate. Does nothing at all on a host that sends nothing. */
    start(): void {
      if (started || !deps.environment) return;
      started = true;
      let purged = false;
      /*
       * Once the region is known: a visitor who once had analytics and no
       * longer does (the record expired, they opted out elsewhere, GPC) keeps
       * nothing from before. Not earlier, because until the region is known
       * analytics is merely not allowed *yet*, and purging then would throw
       * away a consenting visitor's identifier on every page load.
       */
      const settle = () => {
        if (!deps.consent.region() || deps.consent.allows('analytics')) return;
        if (client || loading) shutdown();
        else if (!purged) {
          purged = true;
          purgeStorage();
        }
      };
      deps.consent.whenAllowed('analytics', schedule);
      deps.consent.subscribe(settle);
      settle();
      deps.onNavigate(followRoute);
    },
    track<E extends AnalyticsEvent>(event: E, properties: AnalyticsEvents[E]): void {
      withClient((ph) => {
        ph.capture(event, { ...properties }, SENT_AT_ONCE.has(event) ? { send_instantly: true } : {});
      });
    },
    identify(userId: string, workspaceId: string): void {
      if (identity?.userId === userId && identity.workspaceId === workspaceId) return;
      identity = { userId, workspaceId };
      if (client) applyIdentity(client);
    },
    /** Sign-out: the next person on this browser is not joined to this one. */
    forget(): void {
      if (!identity) return;
      identity = null;
      client?.reset();
    },
  };
}

export type Analytics = ReturnType<typeof createAnalytics>;

function browserDeps(): AnalyticsDeps {
  return {
    consent,
    environment: typeof window === 'undefined' ? null : analyticsEnvironment(window.location.hostname),
    load: () => import('posthog-js').then((module) => module.default),
    whenIdle: (work) => {
      const idle = () => {
        // Safari has no requestIdleCallback; a timer after load is the nearest thing.
        if (typeof window.requestIdleCallback === 'function') {
          // The timeout keeps a page that is never idle (a long animation) from never starting.
          window.requestIdleCallback(() => work(), { timeout: 4000 });
        } else {
          window.setTimeout(work, 1);
        }
      };
      if (document.readyState === 'complete') idle();
      else window.addEventListener('load', idle, { once: true });
    },
    pathname: () => window.location.pathname,
    // The router announces every move with a popstate event (router.tsx).
    onNavigate: (listener) => {
      window.addEventListener('popstate', listener);
      return () => window.removeEventListener('popstate', listener);
    },
    local: () => (typeof localStorage === 'undefined' ? null : localStorage),
    session: () => (typeof sessionStorage === 'undefined' ? null : sessionStorage),
    cookieNames: () =>
      document.cookie
        .split(';')
        .map((part) => part.split('=')[0]?.trim() ?? '')
        .filter(Boolean),
    expireCookie: (name) => {
      // PostHog writes its cookie on the widest domain it can (".krevya.com",
      // shared by the landing page and the app), so it is expired there and on
      // every narrower one as well.
      const parts = window.location.hostname.split('.');
      const domains = [''];
      for (let index = 0; index < parts.length - 1; index += 1) {
        domains.push(`; Domain=.${parts.slice(index).join('.')}`);
      }
      for (const domain of domains) {
        document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${domain}`;
      }
    },
  };
}

/** The page's one instance. Creating it touches nothing; `start()` arms it. */
export const analytics: Analytics = createAnalytics(browserDeps());

export function track<E extends AnalyticsEvent>(event: E, properties: AnalyticsEvents[E]): void {
  analytics.track(event, properties);
}
